import React from 'react';
import moment from 'moment';
import { Button, IconButton } from '@lendi-ui/button';
import Edit from '@lendi-ui/icon/Edit';
import Lock from '@lendi-ui/icon/Lock';
import Tooltip from '@lendi-ui/tooltip';
import { Body, Heading } from '@lendi-ui/typography';

import { APPLICATION_DEFAULT_NAME, DATE_FORMAT_DMY_KMS } from '@funnels/constants';
import { useAnalytics } from '@funnels/providers';
import { Stack } from '@funnels/ui-components';
import { run } from '@funnels/utils';

import { Container, DateLabel, Name, NameContainer, ShowHideButton } from './index.style';

interface CardProps {
  applicationClientId: string;
  createdAt?: string;
  isDisabled?: boolean;
  isSelected: boolean;
  isVisible?: boolean;
  name?: string;
  type?: string;
  updatedAt?: string;
  isLocked?: boolean;
  onContainerClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onContinueClick: (event: React.SyntheticEvent<Element, Event>) => void;
  onShowHideClick?: (event: React.SyntheticEvent<Element, Event>) => void;
  onEditTitleClick: (event: React.SyntheticEvent<Element, Event>) => void;
  isTeamView: boolean;
}

const LABELS = {
  HIDE: 'Hide application',
  SHOW: 'Show application',
  CONTINUE: 'Continue',
  VIEW: 'View',
};

function Card(props: CardProps) {
  const { tracking } = useAnalytics();
  const {
    applicationClientId,
    createdAt,
    isDisabled = false,
    isSelected,
    isVisible,
    type,
    updatedAt,
    isLocked,
    onContainerClick,
    onContinueClick,
    onShowHideClick,
    onEditTitleClick,
    isTeamView,
  } = props;
  let { name } = props;
  const showHideLabel = isVisible ? LABELS.HIDE : LABELS.SHOW;
  const formattedCreatedAt = createdAt ? moment(createdAt).format(DATE_FORMAT_DMY_KMS) : '--/--/----';
  const formattedUpdatedAt = updatedAt ? moment(updatedAt).format(DATE_FORMAT_DMY_KMS) : '--/--/----';

  if (name === APPLICATION_DEFAULT_NAME) {
    name = 'Rename application';
  }

  return (
    <Container
      data-component={`application-card-${applicationClientId}`}
      isDisabled={isDisabled}
      isSelected={isSelected}
      onClick={!isDisabled ? onContainerClick : undefined}
    >
      <Stack halign="justify">
        <Heading size="xs">{type}</Heading>
        {isTeamView && (
          <ShowHideButton onClick={run(onShowHideClick, () => tracking?.button.empty({ text: showHideLabel }))}>
            {showHideLabel}
          </ShowHideButton>
        )}
      </Stack>
      <Stack halign="justify" valign="center">
        <NameContainer valign="center">
          <Stack valign="center">
            <Name>{name}</Name>
            <IconButton aria-label="edit title" size="xs" icon={Edit} onClick={onEditTitleClick} />
          </Stack>
          {isLocked && (
            <Tooltip content="Changes cannot be made to this application as it has settled." position="left">
              <Lock />
            </Tooltip>
          )}
        </NameContainer>
        <Body size="xs">
          <DateLabel>Started:</DateLabel> {formattedCreatedAt}
          <br />
          <DateLabel>Updated:</DateLabel> {formattedUpdatedAt}
        </Body>
        <Button
          variant="primary"
          size="xs"
          onClick={run(onContinueClick, () => tracking?.button.primary({ text: LABELS.CONTINUE }))}
        >
          {isLocked ? LABELS.VIEW : LABELS.CONTINUE}
        </Button>
      </Stack>
    </Container>
  );
}

export default Card;
